import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { showActionErrorFromUnknown } from "@/lib/feedback";
import { AlertCircle, MapPin } from "lucide-react";
import { Drawer } from "@/components/organisms/Drawer";
import { Button } from "@/components/atoms/Button";
import { Input } from "@/components/atoms/Input";
import { TextArea } from "@/components/atoms/TextArea";
import { MenuSelect } from "@/components/molecules/MenuSelect";
import { branchesService } from "@/services/branchesService";
import { contactsService } from "@/services/contactsService";
import { diaryService } from "@/services/diaryService";
import { usersService } from "@/services/usersService";
import type { LegalEntityHierarchy } from "@/services/profileService";
import { propertiesService } from "@/services/propertiesService";
import { useProfileStore } from "@/store/profileStore";
import type { ContactSummary, PropertySummary, UserSummary } from "@/types/entities";
import { contactPropertyListScope } from "@/lib/contactPropertyListScope";
import { propertyDisplayLabel } from "@/lib/propertyDisplay";
import { defaultDiarySlotForDate, fromDatetimeLocalToApi, parseApiDateTime, todayDatetimeLocalMin } from "@/utils/date-helpers";
import { normalizeSyncAltoStatus, type SyncAltoStatus } from "@/lib/syncAlto";
import { buildDiaryEntryCreatePayload, DIARY_EVENT_TYPE_OPTIONS } from "./diaryEntryUtils";
import { DiaryDateTimeField } from "./DiaryDateTimeField";
import { DiaryPropertyCard } from "./DiaryPropertyCard";

const PAGE_SIZE = 30;

const APPLICANT_SYNC_LABELS: Record<SyncAltoStatus, string> = {
  synced: "Synced",
  in_progress: "Syncing",
  pending: "Pending",
  paused: "Paused",
  checkpoint: "Checkpoint",
  failed: "Sync failed",
  unknown: "Sync pending",
};

function diaryApplicantOptionState(contact: ContactSummary) {
  const status = normalizeSyncAltoStatus(contact.crmSyncStatus ?? contact.syncAlto);
  if (status === "synced") {
    return { disabled: false as const, disabledTooltip: undefined };
  }
  return {
    disabled: true as const,
    disabledTooltip: `Contact sync status is ${APPLICANT_SYNC_LABELS[status]}. Only synced contacts can be selected as an applicant.`,
  };
}

/** Backend may use camelCase or snake_case; profile hierarchy uses optional sourceCrm. */
function coalesceSourceCrm(entity: unknown): string | null {
  if (!entity || typeof entity !== "object") return null;
  const o = entity as Record<string, unknown>;
  const v = o.sourceCrm ?? o.sourceCRM ?? o.source_crm;
  return typeof v === "string" && v.trim() ? v.trim() : null;
}

function findLegalEntityNode(
  entities: LegalEntityHierarchy[],
  id: string,
): LegalEntityHierarchy | null {
  for (const le of entities) {
    if (le.id === id) return le;
    const sub = findLegalEntityNode(le.subEntities ?? [], id);
    if (sub) return sub;
  }
  return null;
}

function sourceCrmFromLegalEntityTree(le: LegalEntityHierarchy | null): string | null {
  if (!le) return null;
  const self = coalesceSourceCrm(le);
  if (self) return self;
  for (const b of le.branches ?? []) {
    const c = coalesceSourceCrm(b);
    if (c) return c;
  }
  return null;
}

function findSourceCrmForBranchInProfile(
  legalEntities: LegalEntityHierarchy[],
  branchId: string,
): string | null {
  const walk = (le: LegalEntityHierarchy): string | null => {
    const hit = le.branches?.find((br) => br.id === branchId);
    const fromB = coalesceSourceCrm(hit);
    if (fromB) return fromB;
    for (const sub of le.subEntities ?? []) {
      const r = walk(sub);
      if (r) return r;
    }
    return null;
  };
  for (const root of legalEntities) {
    const r = walk(root);
    if (r) return r;
  }
  return null;
}

interface Props {
  open: boolean;
  date: Date | null;
  legalEntityId?: string;
  branchId?: string;
  onClose: () => void;
  onCreated?: () => void;
}

export function DiaryCreateDrawer({ open, date, legalEntityId, branchId, onClose, onCreated }: Props) {
  const nonGeneralTypeOptions = useMemo(
    () => DIARY_EVENT_TYPE_OPTIONS.filter((o) => o.value !== "GENERAL"),
    [],
  );
  const [title, setTitle] = useState("");
  const [type, setType] = useState<string>("GENERAL");
  const [startLocal, setStartLocal] = useState("");
  const [endLocal, setEndLocal] = useState("");
  const [selectedPropertyId, setSelectedPropertyId] = useState("");
  const [properties, setProperties] = useState<PropertySummary[]>([]);
  const [loadingProperties, setLoadingProperties] = useState(false);
  const [propertiesPage, setPropertiesPage] = useState(0);
  const [propertiesHasNext, setPropertiesHasNext] = useState(false);
  const [propertiesQuery, setPropertiesQuery] = useState("");
  const [contacts, setContacts] = useState<ContactSummary[]>([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [contactsPage, setContactsPage] = useState(0);
  const [contactsHasNext, setContactsHasNext] = useState(false);
  const [contactsQuery, setContactsQuery] = useState("");
  const [selectedApplicantId, setSelectedApplicantId] = useState("");
  const [negotiators, setNegotiators] = useState<UserSummary[]>([]);
  const [loadingNegotiators, setLoadingNegotiators] = useState(false);
  const [negotiatorsPage, setNegotiatorsPage] = useState(0);
  const [negotiatorsHasNext, setNegotiatorsHasNext] = useState(false);
  const [negotiatorsQuery, setNegotiatorsQuery] = useState("");
  const [selectedNegotiatorId, setSelectedNegotiatorId] = useState("");
  const [branchSourceCrm, setBranchSourceCrm] = useState<string | null>(null);
  const [diaryNotes, setDiaryNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [appointmentTypeOptions, setAppointmentTypeOptions] =
    useState<Array<{ value: string; label: string }>>(nonGeneralTypeOptions);

  useEffect(() => {
    if (!open || !date) return;
    const slot = defaultDiarySlotForDate(date);
    setTitle("");
    setType("GENERAL");
    setStartLocal(slot.start);
    setEndLocal(slot.end);
    setSelectedPropertyId("");
    setSelectedApplicantId("");
    setSelectedNegotiatorId("");
    setDiaryNotes("");
    setShowValidation(false);
  }, [open, date]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    diaryService
      .appointmentTypes()
      .then((types) => {
        if (cancelled) return;
        const filtered = types.filter((t) => t.value !== "GENERAL");
        if (!filtered.length) {
          setAppointmentTypeOptions(nonGeneralTypeOptions);
          return;
        }
        setAppointmentTypeOptions(filtered);
        setType((prev) => (filtered.some((t) => t.value === prev) ? prev : filtered[0].value));
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Failed to load appointment types", err);
        setAppointmentTypeOptions(nonGeneralTypeOptions);
      });
    return () => {
      cancelled = true;
    };
  }, [open, nonGeneralTypeOptions]);

  const profileData = useProfileStore((s) => s.profileData);

  useEffect(() => {
    if (!open || !branchId) {
      setBranchSourceCrm(null);
      return;
    }
    branchesService
      .getOne(branchId)
      .then((b) => setBranchSourceCrm(coalesceSourceCrm(b) ?? null))
      .catch(() => setBranchSourceCrm(null));
  }, [open, branchId]);

  const contactsFetchGeneration = useRef(0);
  const negotiatorsFetchGeneration = useRef(0);
  const propertiesFetchGeneration = useRef(0);

  useEffect(() => {
    if (!open) {
      contactsFetchGeneration.current += 1;
      negotiatorsFetchGeneration.current += 1;
      propertiesFetchGeneration.current += 1;
      setContacts([]);
      setNegotiators([]);
      setProperties([]);
      setContactsPage(0);
      setNegotiatorsPage(0);
      setPropertiesPage(0);
      setContactsHasNext(false);
      setNegotiatorsHasNext(false);
      setPropertiesHasNext(false);
    }
  }, [open]);

  const fetchContacts = useCallback(
    (trimmedSearch: string, page = 0, append = false) => {
      const gen = ++contactsFetchGeneration.current;
      const q = trimmedSearch.trim();
      setLoadingContacts(true);
      contactsService
        .list(
          {
            ...contactPropertyListScope(legalEntityId, branchId),
            ...(q ? { search: q } : {}),
          },
          { page, size: PAGE_SIZE },
        )
        .then((res) => {
          if (gen !== contactsFetchGeneration.current) return;
          const content = res.content ?? [];
          setContacts((prev) => {
            const base = append ? [...prev, ...content] : [...content];
            if (selectedApplicantId && !base.some((c) => c.id === selectedApplicantId)) {
              const existing = prev.find((c) => c.id === selectedApplicantId);
              if (existing) base.unshift(existing);
            }
            return base;
          });
          setContactsHasNext(Boolean(res.hasNext));
          setContactsPage(page);
        })
        .catch((err) => {
          if (gen !== contactsFetchGeneration.current) return;
          console.error("Failed to load contacts", err);
        })
        .finally(() => {
          if (gen !== contactsFetchGeneration.current) return;
          setLoadingContacts(false);
        });
    },
    [legalEntityId, branchId, selectedApplicantId],
  );

  const handleContactSearch = useCallback(
    (q: string) => {
      setContactsQuery(q);
      fetchContacts(q, 0, false);
    },
    [fetchContacts],
  );

  const handleContactEnd = useCallback(() => {
    if (contactsHasNext && !loadingContacts) {
      fetchContacts(contactsQuery, contactsPage + 1, true);
    }
  }, [contactsHasNext, loadingContacts, fetchContacts, contactsQuery, contactsPage]);

  const fetchNegotiators = useCallback(
    (trimmedSearch: string, page = 0, append = false) => {
      const gen = ++negotiatorsFetchGeneration.current;
      const q = trimmedSearch.trim();
      setLoadingNegotiators(true);
      usersService
        .list(
          {
            legalEntityId,
            user_type: "NEGOTIATOR",
            ...(q ? { search: q } : {}),
          },
          { page, size: PAGE_SIZE },
        )
        .then((res) => {
          if (gen !== negotiatorsFetchGeneration.current) return;
          const content = res.content ?? [];
          setNegotiators((prev) => {
            const base = append ? [...prev, ...content] : [...content];
            if (selectedNegotiatorId && !base.some((u) => u.id === selectedNegotiatorId)) {
              const existing = prev.find((u) => u.id === selectedNegotiatorId);
              if (existing) base.unshift(existing);
            }
            return base;
          });
          setNegotiatorsHasNext(Boolean(res.hasNext));
          setNegotiatorsPage(page);
        })
        .catch((err) => {
          if (gen !== negotiatorsFetchGeneration.current) return;
          console.error("Failed to load users", err);
        })
        .finally(() => {
          if (gen !== negotiatorsFetchGeneration.current) return;
          setLoadingNegotiators(false);
        });
    },
    [legalEntityId, selectedNegotiatorId],
  );

  const handleNegotiatorSearch = useCallback(
    (q: string) => {
      setNegotiatorsQuery(q);
      fetchNegotiators(q, 0, false);
    },
    [fetchNegotiators],
  );

  const handleNegotiatorEnd = useCallback(() => {
    if (negotiatorsHasNext && !loadingNegotiators) {
      fetchNegotiators(negotiatorsQuery, negotiatorsPage + 1, true);
    }
  }, [negotiatorsHasNext, loadingNegotiators, fetchNegotiators, negotiatorsQuery, negotiatorsPage]);

  const fetchProperties = useCallback(
    (trimmedSearch: string, page = 0, append = false) => {
      const gen = ++propertiesFetchGeneration.current;
      const q = trimmedSearch.trim();
      setLoadingProperties(true);
      propertiesService
        .list(
          {
            ...contactPropertyListScope(legalEntityId, branchId),
            ...(q ? { search: q } : {}),
          },
          { page, size: PAGE_SIZE },
        )
        .then((res) => {
          if (gen !== propertiesFetchGeneration.current) return;
          const content = res.content ?? [];
          setProperties((prev) => {
            const base = append ? [...prev, ...content] : [...content];
            if (selectedPropertyId && !base.some((p) => p.id === selectedPropertyId)) {
              const existing = prev.find((p) => p.id === selectedPropertyId);
              if (existing) base.unshift(existing);
            }
            return base;
          });
          setPropertiesHasNext(Boolean(res.hasNext));
          setPropertiesPage(page);
        })
        .catch((err) => {
          if (gen !== propertiesFetchGeneration.current) return;
          console.error("Failed to load properties", err);
        })
        .finally(() => {
          if (gen !== propertiesFetchGeneration.current) return;
          setLoadingProperties(false);
        });
    },
    [legalEntityId, branchId, selectedPropertyId],
  );

  const handlePropertySearch = useCallback(
    (q: string) => {
      setPropertiesQuery(q);
      fetchProperties(q, 0, false);
    },
    [fetchProperties],
  );

  const handlePropertyEnd = useCallback(() => {
    if (propertiesHasNext && !loadingProperties) {
      fetchProperties(propertiesQuery, propertiesPage + 1, true);
    }
  }, [propertiesHasNext, loadingProperties, fetchProperties, propertiesQuery, propertiesPage]);

  const selectedProperty = useMemo(
    () => properties.find((p) => p.id === selectedPropertyId) ?? null,
    [properties, selectedPropertyId],
  );

  const selectedContact = useMemo(
    () => contacts.find((c) => c.id === selectedApplicantId) ?? null,
    [contacts, selectedApplicantId],
  );

  const selectedNegotiator = useMemo(
    () => negotiators.find((u) => u.id === selectedNegotiatorId) ?? null,
    [negotiators, selectedNegotiatorId],
  );

  const applicantOptions = useMemo(
    () =>
      contacts.map((c) => {
        const primary =
          (c.name && c.name.trim()) ||
          (c.contactPerson && String(c.contactPerson).trim()) ||
          (c.email && c.email.trim()) ||
          `(Contact ${c.externalId || "—"})`;
        const bits = [
          c.category && String(c.category).trim(),
          c.town || c.postcode,
        ].filter(Boolean);
        const suffix = bits.length ? ` — ${bits.join(" · ")}` : "";
        const { disabled, disabledTooltip } = diaryApplicantOptionState(c);
        return {
          value: c.id,
          label: `${primary}${suffix}`,
          disabled,
          disabledTooltip,
        };
      }),
    [contacts],
  );

  const negotiatorOptions = useMemo(
    () =>
      negotiators.map((u) => {
        const label =
          (u.name && u.name.trim()) ||
          (u.email && u.email.trim()) ||
          `(User ${u.externalId || "—"})`;
        return { value: u.id, label };
      }),
    [negotiators],
  );

  const propertyOptions = useMemo(
    () =>
      properties.map((p) => ({
        value: p.id,
        label: propertyDisplayLabel(p),
      })),
    [properties],
  );

  const profileBranchSourceCrm = useMemo(
    () =>
      branchId && profileData ? findSourceCrmForBranchInProfile(profileData.legalEntities, branchId) : null,
    [branchId, profileData],
  );

  const profileLegalEntitySourceCrm = useMemo(() => {
    if (!legalEntityId || !profileData) return null;
    const node = findLegalEntityNode(profileData.legalEntities, legalEntityId);
    return sourceCrmFromLegalEntityTree(node);
  }, [legalEntityId, profileData]);

  const firstPropertySourceCrm = useMemo(() => {
    for (const p of properties) {
      const c = coalesceSourceCrm(p);
      if (c) return c;
    }
    return null;
  }, [properties]);

  const resolvedSourceCrm = useMemo(() => {
    return (
      coalesceSourceCrm(selectedContact) ??
      coalesceSourceCrm(selectedNegotiator) ??
      coalesceSourceCrm(selectedProperty) ??
      (branchSourceCrm?.trim() ? branchSourceCrm.trim() : null) ??
      profileBranchSourceCrm ??
      profileLegalEntitySourceCrm ??
      firstPropertySourceCrm ??
      ""
    );
  }, [
    selectedContact,
    selectedNegotiator,
    selectedProperty,
    branchSourceCrm,
    profileBranchSourceCrm,
    profileLegalEntitySourceCrm,
    firstPropertySourceCrm,
  ]);

  if (!open) return null;

  const dateLabel = date?.toLocaleDateString(undefined, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  const isViewingType = type.trim().toUpperCase() === "VIEWING";

  const trimmedTitle = title.trim();
  const apiStart = fromDatetimeLocalToApi(startLocal);
  const apiEnd = fromDatetimeLocalToApi(endLocal);
  const startDt = apiStart ? parseApiDateTime(apiStart) : null;
  const endDt = apiEnd ? parseApiDateTime(apiEnd) : null;
  const now = new Date();

  const startError =
    !apiStart || !startDt
      ? "Enter a valid start time"
      : startDt.getTime() < now.getTime()
        ? "Start date cannot be in the past"
        : null;
  const endError =
    !apiEnd || !endDt
      ? "Enter a valid end time"
      : startDt && endDt.getTime() <= startDt.getTime()
        ? "End time must be after start time"
        : endDt.getTime() < now.getTime()
          ? "End date cannot be in the past"
          : null;

  const fieldErrors = {
    title: trimmedTitle ? null : "Title is required",
    negotiator: selectedNegotiatorId ? null : "Negotiator is required",
    applicant:
      isViewingType && !selectedApplicantId
        ? "Applicant is required for a viewing"
        : null,
    property:
      isViewingType && !selectedProperty?.id
        ? "Property is required for a viewing"
        : null,
    start: startError,
    end: endError,
  };
  const hasFieldErrors = Object.values(fieldErrors).some(Boolean);

  const handleCreate = async () => {
    setShowValidation(true);
    if (hasFieldErrors) return;

    const sourceCrm = resolvedSourceCrm;
    if (!sourceCrm) {
      const hint = !branchId?.trim()
        ? 'Select a specific branch in the header (not "All branches") so we can resolve the CRM, or rely on loaded property/contact metadata.'
        : "Source CRM is still unavailable. Confirm GET /branches/{id} or your profile hierarchy includes sourceCrm for this branch.";
      toast.error(`Could not resolve source CRM. ${hint}`);
      return;
    }

    if (!legalEntityId?.trim()) {
      toast.error("Select a legal entity before creating a diary entry.");
      return;
    }

    setSaving(true);
    try {
      let payload;
      try {
        payload = buildDiaryEntryCreatePayload({
          title: trimmedTitle,
          type,
          startDate: apiStart ?? "",
          endDate: apiEnd ?? "",
          legalEntityId: legalEntityId.trim(),
          sourceCrm,
          branchId,
          contact: selectedContact,
          user: selectedNegotiator,
          property: selectedProperty,
          diaryNotes,
        });
      } catch (buildErr) {
        const msg = buildErr instanceof Error ? buildErr.message : "Invalid diary entry data.";
        toast.error(msg);
        return;
      }

      await diaryService.create(payload);
      toast.success("Event created.");
      onCreated?.();
      onClose();
    } catch (err) {
      console.error(err);
      showActionErrorFromUnknown(err, "Could not create event. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer open={open} onClose={onClose} title="New diary event">
      <div className="flex flex-col gap-5 p-6 overflow-auto">
        {dateLabel && <p className="text-sm text-bridge-muted">{dateLabel}</p>}

        <div>
          <label className="text-xs font-medium text-bridge-muted block mb-1">
            Title <span className="text-red-500">*</span>
          </label>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Viewing"
            disabled={saving}
            className="w-full"
            required
          />
          {showValidation && <DiaryFieldError message={fieldErrors.title} />}
        </div>

        <div>
          <label className="text-xs font-medium text-bridge-muted block mb-1">Type</label>
          <MenuSelect
            hideLabel
            value={type}
            onChange={setType}
            options={appointmentTypeOptions}
            placeholder="Select type"
            disabled={saving}
            searchable
            searchInTrigger
            searchPlaceholder="Search types…"
            searchDebounceMs={200}
          />
        </div>

        <div>
          <label className="text-xs font-medium text-bridge-muted block mb-1">Diary notes</label>
          <TextArea
            value={diaryNotes}
            onChange={(e) => setDiaryNotes(e.target.value)}
            placeholder="Internal notes for this appointment"
            rows={4}
            className="w-full"
            disabled={saving}
          />
        </div>

        <div>
          <label className="text-xs font-medium text-bridge-muted block mb-1">
            Applicant {isViewingType ? <span className="text-red-500">*</span> : null}
          </label>
          <MenuSelect
            label="Applicant"
            hideLabel
            value={selectedApplicantId}
            onChange={setSelectedApplicantId}
            onOpen={() => {
              fetchContacts(contactsQuery, 0, false);
            }}
            options={applicantOptions}
            placeholder={
              applicantOptions.length
                ? "Select an applicant"
                : loadingContacts
                  ? "Loading contacts…"
                  : "Open and search contacts"
            }
            disabled={saving}
            searchable
            searchInTrigger
            searchPlaceholder="Search contacts…"
            searchDebounceMs={300}
            onDropdownSearchQueryChange={handleContactSearch}
            onDropdownEndReached={handleContactEnd}
            loading={loadingContacts}
          />
          {showValidation && <DiaryFieldError message={fieldErrors.applicant} />}
        </div>

        <div>
          <label className="text-xs font-medium text-bridge-muted block mb-1">
            Negotiator <span className="text-red-500">*</span>
          </label>
          <MenuSelect
            label="Negotiator"
            hideLabel
            value={selectedNegotiatorId}
            onChange={setSelectedNegotiatorId}
            onOpen={() => {
              fetchNegotiators(negotiatorsQuery, 0, false);
            }}
            options={negotiatorOptions}
            placeholder={
              negotiatorOptions.length
                ? "Select a user"
                : loadingNegotiators
                  ? "Loading users…"
                  : "Open and search users"
            }
            disabled={saving}
            searchable
            searchInTrigger
            searchPlaceholder="Search users…"
            searchDebounceMs={300}
            onDropdownSearchQueryChange={handleNegotiatorSearch}
            onDropdownEndReached={handleNegotiatorEnd}
            loading={loadingNegotiators}
          />
          {showValidation && <DiaryFieldError message={fieldErrors.negotiator} />}
        </div>

        <div className="flex flex-col gap-4 sm:flex-row sm:gap-4">
          <div className="min-w-0 flex-1">
            <DiaryDateTimeField
              id="diary-create-start"
              label={
                <span className="flex items-center gap-1">
                  <span>Start</span>
                  <span className="text-red-500">*</span>
                </span>
              }
              value={startLocal}
              onChange={setStartLocal}
              disabled={saving}
              min={todayDatetimeLocalMin()}
            />
            {showValidation && <DiaryFieldError message={fieldErrors.start} />}
          </div>
          <div className="min-w-0 flex-1">
            <DiaryDateTimeField
              id="diary-create-end"
              label={
                <span className="flex items-center gap-1">
                  <span>End</span>
                  <span className="text-red-500">*</span>
                </span>
              }
              value={endLocal}
              onChange={setEndLocal}
              disabled={saving}
              min={todayDatetimeLocalMin()}
            />
            {showValidation && <DiaryFieldError message={fieldErrors.end} />}
          </div>
        </div>

        <div>
          <h3 className="text-sm font-semibold text-bridge-fg flex items-center gap-2 mb-3">
            <span className="text-bridge-muted">
              <MapPin size={16} />
            </span>
            The Property {isViewingType ? <span className="text-red-500">*</span> : null}
          </h3>
          <MenuSelect
            label="Property"
            hideLabel
            value={selectedPropertyId}
            onChange={setSelectedPropertyId}
            onOpen={() => {
              fetchProperties(propertiesQuery, 0, false);
            }}
            options={propertyOptions}
            placeholder={
              propertyOptions.length
                ? "Select a property"
                : loadingProperties
                  ? "Loading properties..."
                  : "Open and search properties"
            }
            disabled={saving}
            searchable
            searchInTrigger
            searchPlaceholder="Search properties..."
            searchDebounceMs={300}
            onDropdownSearchQueryChange={handlePropertySearch}
            onDropdownEndReached={handlePropertyEnd}
            loading={loadingProperties}
          />
          {showValidation && <DiaryFieldError message={fieldErrors.property} />}
          {selectedProperty ? (
            <ul className="flex flex-col gap-3 mt-3">
              <DiaryPropertyCard
                property={selectedProperty}
                onRemove={() => setSelectedPropertyId("")}
              />
            </ul>
          ) : (
            <p className="text-sm text-bridge-muted italic mt-2">No property linked.</p>
          )}
        </div>

        <div className="flex flex-wrap gap-2 pt-2">
          <Button type="button" variant="primary" loading={saving} onClick={handleCreate}>
            Create event
          </Button>
          <Button type="button" variant="secondary" disabled={saving} onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </Drawer>
  );
}

function DiaryFieldError({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <div className="mt-1 flex animate-in items-center gap-1.5 text-xs font-medium text-red-500 fade-in slide-in-from-top-1 duration-200">
      <AlertCircle className="h-3.5 w-3.5 shrink-0" />
      <span>{message}</span>
    </div>
  );
}
